import {
  CoreAnalysisEngine,
  AnalysisResult,
  RecordingResponse,
} from "./coreAnalysisEngine";
import { ReactElement } from "react";
import { TFunction } from "i18next";
import { ResultDashboard } from "../components/molecules/ResultDashboard";
import {
  EngineType,
  ResultDashboardData,
  ResultDiagramIndex,
  ResultMainParam,
  ResultSubParam,
  ResultSupplementaryInfo,
} from "../types";
import { APP_TYPE } from "../environments";

export type Mi1AnalysisResult = AnalysisResult<"mi1">;

const DEFAULT_DASHBOARD_MAIN_PARAM: ResultMainParam = {
  title: "Result.Mi1_vitality",
  // border: "solid 3px #EFB6CB",
  value: undefined,
};

const SUB_PARAMS_TITLE_LIST = ["Result.Mi1_vivacity", "Result.Mi1_relaxation"];

const SUPPLEMENTARY_INFO_TITLE_LIST = [
  "Result.analyze_files",
  "Result.analyze_utterances",
];

export class Mi1Engine extends CoreAnalysisEngine<"mi1"> {
  _engineType: EngineType = "mi1";

  constructor() {
    super("mi1");
  }

  private static dashboardDiagramIndex(t: TFunction): ResultDiagramIndex[] {
    return [
      { width: "32px", title: "No" },
      { width: "40%", title: t("Result.table_phrase"), align: "left" },
      { width: "10%", title: t("Result.Mi1_emotion_Joy") },
      { width: "10%", title: t("Result.Mi1_emotion_Ang") },
      { width: "10%", title: t("Result.Mi1_emotion_Srw") },
      { width: "10%", title: t("Result.Mi1_emotion_Clm") },
      { width: "10%", title: t("Result.Mi1_emotion_Exc") },
      { width: "10%", title: "sec" },
    ];
  }

  private static errorDashboardData(t: TFunction): ResultDashboardData {
    return {
      mainParam: DEFAULT_DASHBOARD_MAIN_PARAM,
      subParams: [
        {
          title: SUB_PARAMS_TITLE_LIST[0],
          value: undefined,
        },
        {
          title: SUB_PARAMS_TITLE_LIST[1],
          value: undefined,
        },
      ],
      supplementaryInfo: [
        {
          title: SUPPLEMENTARY_INFO_TITLE_LIST[0],
          value: undefined,
        },
        {
          title: SUPPLEMENTARY_INFO_TITLE_LIST[1],
          value: undefined,
        },
      ],
      diagramIndex: this.dashboardDiagramIndex(t),
      diagramParams: [],
    };
  }

  private static convertToDashboardData(
    result: Mi1AnalysisResult,
    recording: RecordingResponse | null,
    t: TFunction,
  ): ResultDashboardData {
    const phraseByFilename = new Map(
      recording?.files.map((f) => [f.filename, f.phrase]) ?? [],
    );

    const emotions = (result.detailed_analysis_result?.emotions ?? []).map(
      (e) => ({ ...e, wav_filename: e.filename }),
    );
    const diagramParams = CoreAnalysisEngine.generateResultDiagramParams(
      emotions,
      (param, recordId, _phraseIndex) => [
        { width: "32px", value: recordId, align: "left" },
        {
          width: "40%",
          value: phraseByFilename.get(param.wav_filename) ?? "Error",
          align: "left",
        },
        { width: "10%", value: param.joy },
        { width: "10%", value: param.ang },
        { width: "10%", value: param.srw },
        { width: "10%", value: param.clm },
        { width: "10%", value: param.exc },
        {
          width: "10%",
          value: ((param.end_time - param.start_time) / 1000).toFixed(1),
        },
      ],
    );

    const mainParam: ResultMainParam = {
      ...DEFAULT_DASHBOARD_MAIN_PARAM,
      value: Math.floor(result.analysis_result.vitality * 100),
    };

    const subParams: ResultSubParam[] = [
      {
        title: SUB_PARAMS_TITLE_LIST[0],
        value: Math.floor(
          100 * (result.detailed_analysis_result?.vivacity ?? 0),
        ),
      },
      {
        title: SUB_PARAMS_TITLE_LIST[1],
        value: Math.floor(
          100 * (result.detailed_analysis_result?.relaxation ?? 0),
        ),
      },
    ];

    const supplementaryInfo: ResultSupplementaryInfo[] = [
      {
        title: SUPPLEMENTARY_INFO_TITLE_LIST[0],
        value: recording?.files.length ?? 0,
      },
      {
        title: SUPPLEMENTARY_INFO_TITLE_LIST[1],
        value: result.analysis_result.utterances,
      },
    ];

    return {
      mainParam,
      subParams,
      supplementaryInfo,
      diagramIndex: Mi1Engine.dashboardDiagramIndex(t),
      diagramParams,
    };
  }

  async renderResult(
    key: number,
    fileId: string,
    t: TFunction,
  ): Promise<ReactElement> {
    const result = await this.getResult(fileId);
    const recording = result ? await this.getRecording(fileId) : null;
    const dashboardData = result
      ? Mi1Engine.convertToDashboardData(result, recording, t)
      : Mi1Engine.errorDashboardData(t);
    return (
      <ResultDashboard
        key={key}
        title={
          APP_TYPE === "ntt"
            ? t("Result.MIMOSYS_analysis_result")
            : t("Result.Mi1_analysis_result")
        }
        result={dashboardData}
      />
    );
  }
}
